export interface OrgLimitsRaw {
  [category: string]: { key: string; value: number }[];
}

export interface OrgSnapshotRaw {
  organizationId?: string;
  features?: Record<string, boolean>;
  limits?: OrgLimitsRaw;
  licenses?: string[];
  products?: {
    apnNumbers?: string[];
    highLevelProducts?: string[];
  };
  integrationTypes?: string[];
  [key: string]: unknown;
}

export interface OrgComparisonRaw {
  sourceOrg?: OrgSnapshotRaw;
  destinationOrg?: OrgSnapshotRaw;
  [key: string]: unknown;
}

type CompactLimitMap = Record<string, Record<string, number>>;
 
type LimitDstValue = number | 'NOT_IN_DEST_EXPORT' | 'KEY_NOT_IN_DEST';

interface LimitFlag {
  cat: string;
  key: string;
  src: number;
  dst: LimitDstValue;
}

export interface OrgDiff {
  sourceId: string;
  destId: string;
  licenses: { src: string[]; dst: string[]; srcOnly: string[]; dstOnly: string[] };
  products: { src: string[]; dst: string[]; srcOnly: string[] };

  limits: {
    src: CompactLimitMap;
    dst: CompactLimitMap;
    flags: LimitFlag[];
  };
}

function toCompactLimitMap(raw: OrgLimitsRaw | undefined | null): CompactLimitMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: CompactLimitMap = {};
  for (const [cat, entries] of Object.entries(raw)) {
    if (Array.isArray(entries)) {
      out[cat] = Object.fromEntries(
        entries
          .filter((e) => e && typeof e.key === 'string')
          .map((e) => [e.key, e.value]),
      );
    }
  }
  return out;
}

export function minimizeOrgComparisonJson(raw: OrgComparisonRaw): OrgDiff {
  const srcSnap = raw.sourceOrg ?? ({} as OrgSnapshotRaw);
  const dstSnap = raw.destinationOrg ?? ({} as OrgSnapshotRaw);

  const srcLicenses: string[] = Array.isArray(srcSnap.licenses) ? srcSnap.licenses : [];
  const dstLicenses: string[] = Array.isArray(dstSnap.licenses) ? dstSnap.licenses : [];
  const srcLicSet = new Set(srcLicenses);
  const dstLicSet = new Set(dstLicenses);

  const srcProducts: string[] = srcSnap.products?.highLevelProducts ?? [];
  const dstProducts: string[] = dstSnap.products?.highLevelProducts ?? [];
  const dstProdSet = new Set(dstProducts);

  const srcLimits = toCompactLimitMap(srcSnap.limits as OrgLimitsRaw);
  const dstLimits = toCompactLimitMap(dstSnap.limits as OrgLimitsRaw);

  const srcCats = Object.keys(srcLimits);
  const dstCats = new Set(Object.keys(dstLimits));

  const flags: LimitFlag[] = [];

  for (const cat of srcCats) {
    if (!dstCats.has(cat)) {
      for (const [key, srcVal] of Object.entries(srcLimits[cat])) {
        flags.push({ cat, key, src: srcVal, dst: 'NOT_IN_DEST_EXPORT' });
      }
    } else {
      for (const [key, srcVal] of Object.entries(srcLimits[cat])) {
        const dstVal = dstLimits[cat]?.[key];
        if (dstVal === undefined) {
          flags.push({ cat, key, src: srcVal, dst: 'KEY_NOT_IN_DEST' });
        } else if (dstVal < srcVal) {
          flags.push({ cat, key, src: srcVal, dst: dstVal });
        }
      }
    }
  }

  return {
    sourceId: srcSnap.organizationId ?? 'unknown',
    destId: dstSnap.organizationId ?? 'unknown',
    licenses: {
      src: srcLicenses,
      dst: dstLicenses,
      srcOnly: srcLicenses.filter((l) => !dstLicSet.has(l)),
      dstOnly: dstLicenses.filter((l) => !srcLicSet.has(l)),
    },
    products: {
      src: srcProducts,
      dst: dstProducts,
      srcOnly: srcProducts.filter((p) => !dstProdSet.has(p)),
    },
    limits: { src: srcLimits, dst: dstLimits, flags },
  };
}

export function buildOrgAnalysisPrompt(diff: OrgDiff): string {
  const flagsBlock = JSON.stringify(diff.limits.flags);
  const limitsBlock = JSON.stringify({ src: diff.limits.src, dst: diff.limits.dst });
  const licBlock = JSON.stringify(diff.licenses);
  const prodBlock = JSON.stringify(diff.products);

  const absentCats = [
    ...new Set(
      diff.limits.flags
        .filter((f) => f.dst === 'NOT_IN_DEST_EXPORT')
        .map((f) => f.cat),
    ),
  ];

  return [
    'Perform a Genesys Cloud Application Readiness Assessment (source → destination migration).',
    '',
    'KNOWLEDGE BASE SOURCE — MANDATORY:',
    'Your ONE AND ONLY source of truth for this assessment is the document named:',
    '  application-readiness-org-comparison-and-sync-policy-knowledge-base.md',
    'That document is in your knowledge base. Retrieve it and use it exclusively.',
    '',
    'DO NOT use any other source. This means:',
    '- Do NOT use general Genesys Cloud product knowledge.',
    '- Do NOT use anything learned outside that document.',
    '- Do NOT infer, assume, or extrapolate beyond what that document explicitly states.',
    '- If a license key, limit category, or product is not covered in that document, write "Needs review" in the Assessment column. Do not guess.',
    '',
    'OUTPUT CONSTRAINTS:',
    '- Use only the assessment vocabulary defined in that document: No sync risk / OK / Aligned / High until verified.',
    '- Do NOT use "Low" to mean "fine" or "no issue".',
    '- Do NOT include citations, document names, section numbers, or source attributions in the output.',
    '- State all findings in plain, direct language inside table cells.',
    '',
    `SOURCE ORG: ${diff.sourceId}`,
    `DEST   ORG: ${diff.destId}`,
    '',
    '=== DATA COMPLETENESS NOTE ===',
    'ALL data from the org export JSON is included below. This is the COMPLETE export — no fields are missing from this upload.',
    `The destination org's Genesys Cloud API export did NOT contain these limit categories: [${absentCats.join(', ') || 'none'}].`,
    'This is a genuine org-level fact (those categories were not returned by the API for the destination org), NOT a missing upload field.',
    'flags with dst="NOT_IN_DEST_EXPORT" = that limit category was genuinely absent from the destination org\'s API export.',
    'flags with dst="KEY_NOT_IN_DEST"    = category exists on dest but this specific key was absent from dest export.',
    'flags with dst=number (< src)        = destination has this limit but it is lower than source.',
    '=== END DATA COMPLETENESS NOTE ===',
    '',
    '--- LICENSES ---',
    licBlock,
    '(srcOnly = source-not-destination gaps to assess; dstOnly = destination extras, not a problem)',
    '',
    '--- PRODUCTS (highLevelProducts) ---',
    prodBlock,
    '(srcOnly = source product lines absent from destination)',
    '',
    `--- LIMITS FLAGS (pre-computed red flags) --- ${diff.limits.flags.length === 0 ? '[EMPTY — limits fully aligned, no action required]' : `[${diff.limits.flags.length} flag(s) found]`}`,
    flagsBlock,
    '',
    '--- LIMITS FULL DATA (src and dst compact maps) ---',
    limitsBlock,
    '',
    'FORMATTING RULES — MANDATORY:',
    '- Every section MUST use a Markdown table. No prose, no bullet lists, no narrative paragraphs.',
    '- All findings go inside table cells. Never write explanatory sentences outside a table.',
    '- Keep table cells short and direct.',
    '',
  ].join('\n');
}
